import React, { FC, useEffect, useState, useCallback } from 'react';

import { useSelector } from 'react-redux';

import { alpha, Box, Slide, Stack, useTheme } from '@mui/material';

import { AppState, useAppDispatch } from '~system/store/configure-store';
import { removeToast } from '~system/store/toast/toast-slice';

import { AppIcon } from '~components/shared/app-icon/app-icon';
import { AppTypography } from '~components/ui/typography/app-typography';

import { ReactComponent as IconInfo } from '~assets/icons/icon-bulb.svg';
import { ReactComponent as IconCheck } from '~assets/icons/icon-check.svg';
import { ReactComponent as IconClose } from '~assets/icons/icon-close.svg';
import { ReactComponent as IconExclamation } from '~assets/icons/icon-exclamation.svg';

type ToastColor = {
  background: string;
  iconBackground: string;
  border: string;
  icon: FC;
};

type ToastItemProps = {
  id: string | number;
  title: string;
  message: string;
  type: string;
  duration?: number;
  index: number;
};

const ToastItem = React.memo(
  ({ id, title, message, type, duration, index }: ToastItemProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const [open, setOpen] = useState(true);

    const getToastStyle = useCallback(
      (type: string): ToastColor => {
        const colorsMap: Record<string, ToastColor> = {
          success: {
            background: theme.palette.green['10'],
            iconBackground: theme.palette.green['125'],
            border: theme.palette.green['125'],
            icon: IconCheck,
          },
          info: {
            background: theme.palette.primaryTokens['10'],
            iconBackground: theme.palette.primaryTokens['125'],
            border: theme.palette.primaryTokens['125'],
            icon: IconInfo,
          },
          warning: {
            background: theme.palette.orange['10'],
            iconBackground: theme.palette.orange['100'],
            border: theme.palette.orange['100'],
            icon: IconExclamation,
          },
          error: {
            background: theme.palette.red['10'],
            iconBackground: theme.palette.red['125'],
            border: theme.palette.red['125'],
            icon: IconClose,
          },
        };

        return colorsMap[type];
      },
      [theme],
    );

    const handleClose = useCallback(() => {
      setOpen(false);
    }, []);

    useEffect(() => {
      if (!duration) return;

      const timer = window.setTimeout(() => {
        setOpen(false);
      }, duration);

      return () => window.clearTimeout(timer);
    }, [duration]);

    const color = getToastStyle(type);

    return (
      <Slide
        direction="down"
        in={open}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 250, exit: 220 }}
        onExited={() => dispatch(removeToast(String(id)))}
        style={{
          transitionDelay: open ? `${index * 50}ms` : '0ms',
        }}
      >
        <Box
          sx={{
            minWidth: '300px',
            maxWidth: '350px',
            width: '100%',
            backgroundColor: {
              xs: alpha(color.background, 0.7),
              sm: alpha(color.background, 0.8),
            },
            color: 'neutral.0',
            borderRadius: '12px',
            padding: {
              xs: theme.spacing(1, 1.5),
              sm: theme.spacing(1.5, 2),
            },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0, sm: 1.5 },
            border: '1px solid',
            borderColor: color.border,
            backdropFilter: {
              xs: 'blur(3px)',
              sm: 'blur(2px)',
            },
            boxSizing: 'border-box',
          }}
        >
          <Box
            sx={{
              width: { xs: 28, sm: 36 },
              height: { xs: 28, sm: 36 },
              minWidth: { xs: 28, sm: 36 },
              minHeight: { xs: 28, sm: 36 },
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'neutral.100',
              backgroundColor: color.iconBackground,
              mr: { xs: 1.25, sm: 0 },
            }}
          >
            <AppIcon component={color.icon} size={{ xs: 20, sm: 24 }} />
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Stack direction="row">
              <AppTypography
                size={{ xs: 'body-sm', sm: 'body-md' }}
                weight="bold"
                color="neutral.0"
              >
                {title}
              </AppTypography>
            </Stack>

            <AppTypography
              size={{ xs: 'body-caption', sm: 'body-sm' }}
              maxWidth={280}
              color="neutral.30"
            >
              {message}
            </AppTypography>
          </Box>

          <Box
            role={'button'}
            onClick={handleClose}
            color="neutral.30"
            sx={{
              cursor: 'pointer',
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 'auto',
            }}
          >
            <AppIcon component={IconClose} size={22} />
          </Box>
        </Box>
      </Slide>
    );
  },
);

ToastItem.displayName = 'ToastItem';

export const Toasts: React.FC = () => {
  const toasts = useSelector((state: AppState) => state.toast.toasts);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: { xs: 60, sm: 20 },
        right: { xs: '50%', sm: 20 },
        transform: { xs: 'translateX(50%)', sm: 'none' },
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '8px',
      }}
    >
      {toasts.map((toast, index) => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          title={toast.title}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
          index={index}
        />
      ))}
    </Box>
  );
};
